"use client";

import { useUser } from "@clerk/nextjs";
import { useEffect, useRef } from "react";
import { useSetLastCartId } from "@/features/cart/api/hooks/use-set-last-cart-id";
import { useCartStore } from "@/features/cart/components";
import { resolveLastCartIdSync } from "@/features/cart/utils/resolve-last-cart-id-sync";
import { useGetUser } from "@/features/panel/hooks/use-get-user";

/*
  Keeps buyer.lastCartId consistent with the classic cart. BE no longer sets this field automatically, frontend does so explicitly and only for the default cart (multidiet/extend-diet have their own stores and are not included here).
 */
export const useSyncLastCartId = () => {
  const { isLoaded, isSignedIn } = useUser();
  const cartId = useCartStore((state) => state.cartId);
  const hasItems = useCartStore((state) => state.items.length > 0);

  const { data: user, isSuccess } = useGetUser({
    enabled: isLoaded && isSignedIn,
  });

  const { mutate: setLastCartId } = useSetLastCartId();

  const syncedRef = useRef<string | null>(null);
  const prevCartIdRef = useRef<string | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!(isLoaded && isSignedIn && isSuccess)) {
      return;
    }

    if (!initializedRef.current) {
      syncedRef.current = user?.lastCartId ?? null;
      initializedRef.current = true;
    }

    const normalizedCartId = cartId ?? null;
    const cartIdChanged = prevCartIdRef.current !== normalizedCartId;
    prevCartIdRef.current = normalizedCartId;

    const result = resolveLastCartIdSync({
      cartId: normalizedCartId,
      hasItems,
      cartIdChanged,
      syncedCartId: syncedRef.current,
    });

    if (!result.shouldSync) {
      return;
    }

    syncedRef.current = result.cartId;
    setLastCartId({ cartId: result.cartId });
  }, [isLoaded, isSignedIn, isSuccess, user, cartId, hasItems, setLastCartId]);
};
